const styleId = 'icf-easter-egg-cross-style'

// A Latin cross on a 100x150 viewBox: the beam is centred at x=50 and runs the
// full height, the crossbar sits in the upper third. The detector accepts only
// crosses drawn in roughly these proportions, so the reveal lands right on top
// of what was drawn.
const beamCenter = 50
const viewBoxWidth = 100
const viewBoxHeight = 150
const crossSvg = `<svg viewBox="0 0 ${viewBoxWidth} ${viewBoxHeight}" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
  <rect x="42" y="0" width="16" height="150" rx="5" fill="currentColor"/>
  <rect x="12" y="34" width="76" height="16" rx="5" fill="currentColor"/>
</svg>`

// Styles are injected from JS on purpose: PurgeCSS only scans
// php/html/jsx/tsx sources, so classes referenced solely in this .ts file
// would be purged from the compiled stylesheets. The scale grows from the
// element's centre (transform-origin default), so the cross settles at exactly
// the drawn size - no translate, since we anchor by explicit left/top.
const css = `
.icf-easter-egg-cross {
  position: fixed;
  z-index: 9999;
  pointer-events: none;
  color: var(--icf-egg-cross-color, #ffd27a);
  filter: drop-shadow(0 0 12px var(--icf-egg-cross-color, #ffd27a));
  opacity: 0;
  animation: icf-easter-egg-cross var(--icf-egg-cross-duration) ease-in-out forwards;
}

.icf-easter-egg-cross > svg {
  display: block;
  width: 100%;
  height: 100%;
}

@keyframes icf-easter-egg-cross {
  0% { opacity: 0; transform: scale(0.75); }
  25% { opacity: 1; transform: scale(1); }
  70% { opacity: 1; transform: scale(1); }
  100% { opacity: 0; transform: scale(1); }
}

/* motion-free fallback: pure opacity fade, no scaling */
@media (prefers-reduced-motion: reduce) {
  .icf-easter-egg-cross {
    animation-name: icf-easter-egg-cross-fade;
  }
  @keyframes icf-easter-egg-cross-fade {
    0% { opacity: 0; }
    25% { opacity: 1; }
    70% { opacity: 1; }
    100% { opacity: 0; }
  }
}
`

function injectStyles() {
  if (document.getElementById(styleId)) return
  const style = document.createElement('style')
  style.id = styleId
  style.textContent = css
  document.head.appendChild(style)
}

/**
 * Reveal a glowing cross exactly over the vertical beam the user drew: same
 * position, same size. The beam is described by its centre x, its top y and its
 * height (all viewport px, as from pointer/touch clientX,Y); the crossbar and
 * width follow from the fixed cross proportions.
 */
export function playCross(options: {
  /** beam centre x (viewport px) */
  x: number
  /** beam top y (viewport px) */
  top: number
  /** beam height = drawn vertical extent (px) */
  height: number
  color?: string
  durationMs?: number
}) {
  const { x, top, height, color = '#ffd27a', durationMs = 2600 } = options
  injectStyles()

  // preserve the viewBox aspect so the beam overlays the drawn one 1:1
  const width = (height * viewBoxWidth) / viewBoxHeight
  const left = x - (width * beamCenter) / viewBoxWidth

  const cross = document.createElement('div')
  cross.className = 'icf-easter-egg-cross'
  cross.setAttribute('aria-hidden', 'true')
  cross.style.left = `${left}px`
  cross.style.top = `${top}px`
  cross.style.width = `${width}px`
  cross.style.height = `${height}px`
  cross.style.setProperty('--icf-egg-cross-color', color)
  cross.style.setProperty('--icf-egg-cross-duration', `${durationMs}ms`)
  cross.innerHTML = crossSvg
  cross.addEventListener('animationend', () => cross.remove())
  // fallback in case the animation never fires (e.g. hidden tab)
  setTimeout(() => cross.remove(), durationMs + 500)

  document.body.appendChild(cross)
}
